import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {Machine} from '@models/machine/machine';
import {TrafficLightConfiguration} from '@models/machine/traffic-light/traffic-light-configuration';
import {Signal} from '@models/dashboard/signal';
import {StatusEventPageService} from '@services/pages/status-event-page/configuration/status-event-page.service';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {MachineTag} from '@models/adminstration/machine/tag/machine-tag';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MachineTagService} from '@services/administration/machine/tag/machine-tag.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';

@Component({
    selector: 'scitis-admin-machine-traffic-light',
    templateUrl: './traffic-light.component.html',
    styleUrls: ['./traffic-light.component.scss'],
    standalone: false
})
export class TrafficLightComponent implements OnChanges {

  @Input()
  public machine: Machine = new Machine();

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  public machineTag: MachineTag | null = null;
  @Input()
  public signalsArray: Signal[] = [];

  public statusEventPagesArray: StatusEventPage[] = [];
  public isLoadingStatusEventPages: boolean = false;
  public isLoadingSignals: boolean = false;
  private CURRENT_PAGE_NAME = 'Machine - TrafficLightConfiguration';
  public isAdminOrLower = true;

  constructor(private statusEventPageService: StatusEventPageService,
              private translate: TranslateService,
              private notification: NotificationService,
              private loadingIndicator: LoadingIndicatorService,
              private machineTagService: MachineTagService,
              private machineService: MachineService) {
  }

  public get shouldShowSaveButton(): boolean {
    if (!this.userRole.isTechAdminRole && !this.userRole.isSuperAdminRole) {
      return false;
    }

    if (this.isSyncTrafficLightSettingsChecked) {
      return this.userRole.isTechAdminRole || this.userRole.isSuperAdminRole;
    }
    return true;
  }

  public get signalNotFoundText(): string {
    if (!this.signalsArray || this.signalsArray.length === 0) {
      return this.translate.instant('There are no available signals to select from. Please add some in the machine configuration');
    }
    return this.translate.instant('No signals found');
  }

  public get signalsPlaceholderText(): string {
    if (!this.signalsArray || this.signalsArray.length === 0) {
      return this.translate.instant('There are no available signals to select from. Please add some in the machine configuration');
    }
    return this.translate.instant('Select signals....');
  }

  public get statusEventPageNotFoundText(): string {
    if (!this.statusEventPagesArray || this.statusEventPagesArray.length === 0) {
      return this.translate.instant('There are no available status event pages to select from. Please add some in the menu.');
    }
    return this.translate.instant('No status event pages found');
  }

  public get statusEventPagePlaceholderText(): string {
    if (!this.statusEventPagesArray || this.statusEventPagesArray.length === 0) {
      return this.translate.instant('There are no available status event pages to select from. Please add some in the menu.');
    }
    return this.translate.instant('Select status event page ....');
  }

  public get isSyncTrafficLightSettingsSliderVisible() {
    return (this.userRole.isTechAdminRole || this.userRole.isSuperAdminRole) && !!this.machineTag;
  }

  public get isSyncTrafficLightSettingsChecked() {
    if (this.machineTag === null) {
      return false;
    }
    return this.machineTag.syncTrafficLightSettings;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isInvalidMachineConfigurationReceived(changes)) {
      return;
    } else {
      if (!this.machine.trafficLightConfiguration) {
        this.machine.trafficLightConfiguration = new TrafficLightConfiguration();
      }
      this.loadStatusEventPages();
    }
    this.isAdminOrLower = !(this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole);
  }

  onSelectSignal($event: Signal) {
    if (!$event) {
      this.machine.trafficLightConfiguration.signalId = -1;
      this.machine.trafficLightConfiguration.signalName = '';
      return;
    }
    this.machine.trafficLightConfiguration.signalId = $event.id;
    this.machine.trafficLightConfiguration.signalName = $event.name;

  }

  setIsUsingSignalAsSource() {
    this.machine.trafficLightConfiguration.usingStatusEventPage = false;
  }

  setIsUsingStatusEventPageAsSource() {
    this.machine.trafficLightConfiguration.usingStatusEventPage = true;
    this.machine.trafficLightConfiguration.signalId = -1;
    this.machine.trafficLightConfiguration.signalName = '';
  }

  onSelectStatusEventPage($event: StatusEventPage) {
    if (!$event) {
      this.machine.trafficLightConfiguration.statusEventPageId = -1;
      this.machine.trafficLightConfiguration.statusEventPageName = '';
      this.machine.trafficLightConfiguration.statusEventPageMenuId = '';
      return;
    }
    this.machine.trafficLightConfiguration.statusEventPageId = $event.id;
    this.machine.trafficLightConfiguration.statusEventPageName = $event.name;
    this.machine.trafficLightConfiguration.statusEventPageMenuId = $event.menuItemId;
  }

  public onSyncTrafficLightSettingsChange($event: any) {
    if (this.machineTag === null) {
      return;
    }
    this.loadingIndicator.show('Changing flag');
    this.machineTag.syncTrafficLightSettings = $event.target.checked;
    this.machineTagService.changeSyncTrafficLightSettingsFlag(this.machineTag.category, this.machineTag.tag, $event.target.checked).subscribe((_result) => {
      let message: string = this.translate.instant('The ApplyToTrafficLightMachinesOfSameTag flag for tag %tag of category %category was updated successfully');
      message = message.replace('%category', this.machine.category.toBold());
      message = message.replace('%tag', this.machine.machineTag ? this.machine.machineTag.toBold() : '');
      this.notification.success(message, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicator.hide();
    }, (error) => {
      let message: string = this.translate.instant('The ApplyToMachinesOfSameTag flag for tag %tag of category %category was not updated ');
      message = message.replace('%category', this.machine.category.toBold());
      message = message.replace('%tag', this.machine.machineTag ? this.machine.machineTag.toBold() : '');
      this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicator.hide();
    });
  }

  onSaveTrafficLightConfiguration() {
    if (!this.machine || !this.machine.id || !this.machine.trafficLightConfiguration) {
      return;
    }

    this.loadingIndicator.show();
    this.machineService.saveTrafficLightConfiguration(this.machine.id, this.machine.trafficLightConfiguration).subscribe(() => {
      let message = this.translate.instant('Machine %machine traffic light configuration saved.');


      if (this.isSyncTrafficLightSettingsChecked) {
        message += '<br/>' + this.translate.instant('The configuration will be applied to all machines with %tag tag of %category category!');
      }

      message = message.replaceAll('%machine', this.machine.name.toBold());
      message = message.replace('%category', this.machine.category.toBold());
      message = message.replace('%tag', this.machine.machineTag ? this.machine.machineTag.toBold() : '');


      this.notification.success(message, this.CURRENT_PAGE_NAME, false);
      this.loadingIndicator.hide();
    }, (error) => {
      this.notification.error(this.translate.instant('Error saving machine traffic light configuration.'), error, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicator.hide();
    });
  }

  private isInvalidMachineConfigurationReceived(changes: SimpleChanges) {
    return !changes || !changes.machine || !changes.machine.currentValue ||
        !changes.machine.currentValue.id || changes.machine.currentValue.id <= 0;
  }

  private loadStatusEventPages() {
    this.isLoadingStatusEventPages = true;
    if (!this.machine || !this.machine.id) {
      this.statusEventPagesArray = [];
      this.isLoadingStatusEventPages = false;
      return;
    }

    this.statusEventPageService.listStatusEventPagesOfMachine(this.machine.id).subscribe((success) => {

      this.statusEventPagesArray = [...success];
      this.isLoadingStatusEventPages = false;
    }, (error) => {
      console.log(error);
      const message = this.translate.instant('Error loading error pages.');
      this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
      this.isLoadingStatusEventPages = false;
    });
  }


}
